import { z } from "zod";
import { MAX_ORG_SCAN_OBJECTS } from "./org-scan.js";
const salesforceIdSchema = z.string().min(15).max(18);
const apiNameSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/);
const shortTextSchema = z.string().trim().min(1).max(240);
export const companyBusinessProfileInputSchema = z.object({
    companyName: shortTextSchema,
    industry: shortTextSchema,
    subIndustry: z.string().trim().max(240).default(""),
    productsAndServices: z.array(shortTextSchema).min(1).max(30),
    customerTypes: z.array(shortTextSchema).min(1).max(20),
    businessModels: z.array(shortTextSchema).min(1).max(20),
    businessTerms: z.array(shortTextSchema).max(50).default([]),
    departments: z.array(shortTextSchema).max(30).default([]),
    businessRoles: z.array(shortTextSchema).max(30).default([]),
    typicalBusinessProcesses: z.array(z.string().trim().min(1).max(1000)).max(30).default([]),
    timeConsumingWork: z.array(z.string().trim().min(1).max(1000)).max(30).default([]),
    businessRules: z.array(z.string().trim().min(1).max(1000)).max(50).default([]),
    aiProhibitedActions: z.array(z.string().trim().min(1).max(1000)).max(50).default([]),
    dataSensitivityNotes: z.string().trim().max(4000).default("")
});
export const companyBusinessProfileSchema = companyBusinessProfileInputSchema.extend({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    version: z.number().int().positive(),
    status: z.enum(["active", "archived"]),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const catalogRecordTypeSchema = z.object({
    id: salesforceIdSchema.optional(),
    name: shortTextSchema,
    developerName: apiNameSchema,
    available: z.boolean(),
    active: z.boolean(),
    defaultRecordTypeMapping: z.boolean()
});
export const catalogRelationshipSchema = z.object({
    fieldApiName: apiNameSchema,
    relationshipName: apiNameSchema.optional(),
    targetObjectApiNames: z.array(apiNameSchema).min(1).max(20),
    polymorphic: z.boolean()
});
export const schemaFingerprintSchema = z.object({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    snapshotId: z.string().uuid(),
    algorithm: z.literal("sha256"),
    value: z.string().regex(/^[a-f0-9]{64}$/),
    selectedObjectApiNames: z.array(apiNameSchema).min(1).max(MAX_ORG_SCAN_OBJECTS),
    createdAt: z.string().datetime()
});
export const orgAnalysisRunSchema = z.object({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    status: z.enum(["running", "completed", "failed"]),
    selectedObjectApiNames: z.array(apiNameSchema).min(1).max(MAX_ORG_SCAN_OBJECTS),
    snapshotId: z.string().uuid().optional(),
    schemaFingerprintId: z.string().uuid().optional(),
    error: z.string().max(1000).optional(),
    createdBy: z.string().uuid(),
    startedAt: z.string().datetime(),
    completedAt: z.string().datetime().optional()
});
export const knowledgeCapabilityStatusSchema = z.enum([
    "available",
    "unavailable",
    "permission_required",
    "api_unsupported",
    "temporarily_unavailable"
]);
export const knowledgeCapabilitySnapshotSchema = z.object({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    salesforceUserId: salesforceIdSchema.optional(),
    source: z.enum(["org_integration", "current_user"]),
    cloudVariant: z.enum(["global", "alibaba_cloud"]),
    apiVersion: z.string().regex(/^v\d{2,3}\.\d$/).optional(),
    status: knowledgeCapabilityStatusSchema,
    reasonCode: z.enum([
        "AVAILABLE",
        "KNOWLEDGE_NOT_ENABLED",
        "CURRENT_USER_GRANT_REQUIRED",
        "INSUFFICIENT_ACCESS",
        "RESOURCE_NOT_SUPPORTED",
        "SALESFORCE_UNAVAILABLE"
    ]),
    articleObjectApiNames: z.array(apiNameSchema).max(100).default([]),
    supportedChannels: z.array(z.enum(["internal", "customer", "partner", "public"])).max(4).default([]),
    supportedLanguages: z.array(z.string().min(2).max(20)).max(100).default([]),
    dataCategoryGroups: z.array(z.object({
        name: apiNameSchema,
        label: shortTextSchema
    })).max(100).default([]),
    userCanRead: z.boolean(),
    checkedAt: z.string().datetime(),
    errorSummary: z.string().max(500).optional()
});
export const knowledgeCapabilityProbeResultSchema = z.object({
    integration: knowledgeCapabilitySnapshotSchema.optional(),
    currentUser: knowledgeCapabilitySnapshotSchema,
    capabilityDiffersByUser: z.boolean()
});
export const businessPatternKeySchema = z.enum([
    "direct_sales",
    "channel_sales",
    "customer_service",
    "field_service"
]);
export const businessGoalPatternSchema = z.object({
    key: businessPatternKeySchema,
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    label: shortTextSchema,
    description: z.string().trim().min(1).max(1000),
    goalHints: z.array(shortTextSchema).min(1).max(20),
    roleHints: z.array(shortTextSchema).max(20),
    operationHints: z.array(z.enum(["query", "aggregate", "create", "update", "delete"])).min(1).max(5)
}).strict();
export const scenarioOperationSchema = z.enum(["query", "aggregate", "create", "update", "delete"]);
export const scenarioDiscoveryInputSchema = z.object({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    version: z.number().int().positive(),
    companyBusinessProfileId: z.string().uuid(),
    companyBusinessProfileVersion: z.number().int().positive(),
    name: shortTextSchema,
    businessGoal: z.string().trim().min(1).max(2000),
    targetRoles: z.array(shortTextSchema).min(1).max(20),
    exampleUtterances: z.array(z.string().trim().min(1).max(1000)).min(1).max(20),
    selectedPatternKeys: z.array(businessPatternKeySchema).min(1).max(4),
    requestedOperations: z.array(scenarioOperationSchema).min(1).max(4),
    knowledgeRequested: z.boolean().default(false),
    status: z.enum(["draft", "generated", "compiled"]),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
}).strict();
export const scenarioDiscoveryInputRequestSchema = scenarioDiscoveryInputSchema.pick({
    name: true,
    businessGoal: true,
    targetRoles: true,
    exampleUtterances: true,
    selectedPatternKeys: true,
    requestedOperations: true,
    knowledgeRequested: true
}).strict();
export const scenarioRecommendationStatusSchema = z.enum([
    "confirmed",
    "recommended",
    "unresolved",
    "unsupported",
    "blocked",
    "excluded"
]);
export const scenarioMappingKindSchema = z.enum([
    "object_role",
    "field_meaning",
    "relationship",
    "record_type_or_picklist",
    "action"
]);
const scenarioMappingCandidateBaseSchema = z.object({
    key: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,119}$/),
    kind: scenarioMappingKindSchema,
    businessMeaning: shortTextSchema,
    objectApiName: apiNameSchema.nullable().optional(),
    fieldApiName: apiNameSchema.nullable().optional(),
    targetObjectApiName: apiNameSchema.nullable().optional(),
    recordTypeDeveloperName: apiNameSchema.nullable().optional(),
    picklistValue: z.string().max(255).nullable().optional(),
    actionKey: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,119}$/).nullable().optional(),
    operation: scenarioOperationSchema.nullable().optional(),
    status: scenarioRecommendationStatusSchema,
    confidence: z.number().min(0).max(1),
    evidence: z.array(z.string().trim().min(1).max(500)).min(1).max(20),
    impact: z.string().trim().min(1).max(1000),
    requiresConfirmation: z.boolean()
}).strict();
function validateScenarioMappingCandidate(mapping, context) {
    if (["unresolved", "unsupported", "blocked", "excluded"].includes(mapping.status))
        return;
    if (!mapping.objectApiName) {
        context.addIssue({ code: "custom", path: ["objectApiName"], message: "已推荐或确认的映射必须指定对象" });
    }
    if (mapping.kind === "field_meaning" && !mapping.fieldApiName) {
        context.addIssue({ code: "custom", path: ["fieldApiName"], message: "字段含义映射必须指定字段" });
    }
    if (mapping.kind === "relationship" && (!mapping.fieldApiName || !mapping.targetObjectApiName)) {
        context.addIssue({ code: "custom", path: ["targetObjectApiName"], message: "关系映射必须指定关系字段和目标对象" });
    }
    if (mapping.kind === "record_type_or_picklist" &&
        !mapping.recordTypeDeveloperName &&
        !(mapping.fieldApiName && mapping.picklistValue !== undefined && mapping.picklistValue !== null)) {
        context.addIssue({ code: "custom", path: ["recordTypeDeveloperName"], message: "Record Type 或状态值映射缺少选择" });
    }
    if (mapping.kind === "action" && (!mapping.actionKey || !mapping.operation)) {
        context.addIssue({ code: "custom", path: ["actionKey"], message: "Action 映射必须指定 Action 和操作" });
    }
}
export const scenarioMappingCandidateSchema = scenarioMappingCandidateBaseSchema
    .superRefine(validateScenarioMappingCandidate);
export const scenarioMappingSchema = scenarioMappingCandidateBaseSchema.extend({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    generatedDraftId: z.string().uuid(),
    version: z.number().int().positive(),
    confirmedBy: z.string().uuid().optional(),
    confirmedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
}).strict().superRefine(validateScenarioMappingCandidate);
export const generatedScenarioDraftInputSchema = z.object({
    name: shortTextSchema,
    purpose: z.string().trim().min(1).max(2000),
    targetRoles: z.array(shortTextSchema).min(1).max(20),
    exampleUtterances: z.array(z.string().trim().min(1).max(1000)).min(1).max(20),
    knowledgePolicy: z.object({
        enabled: z.boolean(),
        languages: z.array(z.string().min(2).max(20)).max(20),
        requireCitations: z.literal(true),
        noAnswerBehavior: z.literal("say_not_found")
    }).strict(),
    requiredMappings: z.array(scenarioMappingCandidateSchema).min(1).max(100),
    steps: z.array(z.object({
        order: z.number().int().min(1).max(5),
        type: z.enum([
            "collect_input",
            "query",
            "summarize",
            "draft",
            "preview_write",
            "execute_action"
        ]),
        actionKey: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,119}$/).nullable().optional()
    }).strict()).min(1).max(5),
    businessRules: z.array(z.string().trim().min(1).max(1000)).max(50),
    confirmationPoints: z.array(z.string().trim().min(1).max(1000)).max(30),
    testCases: z.array(z.object({
        input: z.string().trim().min(1).max(2000),
        expectedOutcome: z.string().trim().min(1).max(2000)
    }).strict()).min(1).max(30),
    unresolvedItems: z.array(z.string().trim().min(1).max(1000)).max(50)
}).strict().superRefine((draft, context) => {
    const orders = draft.steps.map(({ order }) => order);
    if (new Set(orders).size !== orders.length || orders.some((order, index) => order !== index + 1)) {
        context.addIssue({ code: "custom", path: ["steps"], message: "场景步骤必须从 1 开始连续编号且不得重复" });
    }
    if (draft.knowledgePolicy.enabled && draft.knowledgePolicy.languages.length === 0) {
        context.addIssue({ code: "custom", path: ["knowledgePolicy", "languages"], message: "启用 Knowledge 时必须确认语言" });
    }
});
export const generatedScenarioDraftSchema = generatedScenarioDraftInputSchema.extend({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    discoveryInputId: z.string().uuid(),
    generationContextSnapshotId: z.string().uuid(),
    version: z.number().int().positive(),
    generatorMode: z.enum(["deterministic", "model"]),
    model: z.string().trim().min(1).max(160),
    promptVersion: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,79}$/),
    status: z.enum(["generated", "confirmed", "compiled", "superseded"]),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
}).strict();
export const generationContextSnapshotSchema = z.object({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    discoveryInputId: z.string().uuid(),
    discoveryInputVersion: z.number().int().positive(),
    companyBusinessProfileId: z.string().uuid(),
    companyBusinessProfileVersion: z.number().int().positive(),
    organizationSnapshotId: z.string().uuid(),
    schemaFingerprintId: z.string().uuid(),
    schemaFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    knowledgeCapabilitySnapshotIds: z.array(z.string().uuid()).max(2).default([]),
    knowledgeSourceConfigId: z.string().uuid().optional(),
    businessPatternVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
    model: z.string().trim().min(1).max(160),
    promptVersion: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,79}$/),
    actionSchemaVersion: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,79}$/),
    mappingVersion: z.number().int().positive(),
    fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    createdAt: z.string().datetime()
}).strict();
export const businessScenarioVersionSchema = z.object({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    businessScenarioId: z.string().uuid(),
    version: z.number().int().positive(),
    generatedDraftId: z.string().uuid(),
    generationContextSnapshotId: z.string().uuid(),
    schemaFingerprintId: z.string().uuid(),
    linkedSkillKey: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,79}$/),
    linkedSkillVersion: z.string().regex(/^\d+\.\d+\.\d+(?:-[a-z0-9.-]+)?$/),
    status: z.enum(["draft", "review_required", "published", "archived"]),
    publishFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    readiness: z.object({
        ready: z.boolean(),
        blockers: z.array(z.string().trim().min(1).max(1000)).max(100)
    }).strict(),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    publishedAt: z.string().datetime().optional()
}).strict();
export const scenarioActionBindingSchema = z.object({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    businessScenarioVersionId: z.string().uuid(),
    actionKey: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,119}$/),
    objectApiName: apiNameSchema,
    operation: scenarioOperationSchema,
    readableFields: z.array(apiNameSchema).max(100),
    writableFields: z.array(apiNameSchema).max(100),
    confirmationRequired: z.boolean(),
    status: z.enum(["confirmed", "blocked"]),
    createdAt: z.string().datetime()
}).strict();
export const scenarioReadinessCheckSchema = z.object({
    id: z.string().uuid(),
    organizationId: salesforceIdSchema,
    businessScenarioVersionId: z.string().uuid(),
    schemaFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    status: z.enum(["passed", "failed"]),
    checks: z.array(z.object({
        key: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,79}$/),
        status: z.enum(["passed", "failed"]),
        summary: z.string().trim().min(1).max(1000)
    }).strict()).min(1).max(100),
    createdAt: z.string().datetime()
}).strict();
